// Team hub — admin's view of clinic members + invite management (Week 6).
//
// - Lists active members with their role
// - Admin can change a member's role via inline dropdown, or Remove them
// - Admin can mint a role-scoped invite code (paste into WhatsApp)
// - Admin can Revoke a still-pending invite (flips expires_at to now)
//
// Server-side guardrails:
//   * cannot_demote_sole_admin — must transfer admin first
//   * cannot_revoke_sole_admin — must transfer admin first
// The UI translates those to user-friendly nudges instead of raw codes.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    ClinicInviteListItem,
    ClinicMemberRole,
    ClinicMemberWithClinician,
    GetMyClinicRes,
} from '@pamosi/shared';
import {
    CLINIC_MEMBER_ROLE_LABELS,
    CLINIC_MEMBER_ROLE_OPTIONS,
} from '@pamosi/shared';
import { ApiError, clinicApi } from '../lib/api';

export function TeamScreen() {
    const { data: mine } = useQuery<GetMyClinicRes>({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const isAdmin = mine?.member?.role === 'admin';
    const selfClinicianId = mine?.member?.clinician_id ?? null;

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">Team</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Members of {mine?.clinic?.facility_name ?? 'your clinic'}.
                </p>
            </div>

            <MembersSection isAdmin={isAdmin} selfClinicianId={selfClinicianId} />

            {isAdmin && (
                <>
                    <NewInviteForm />
                    <InvitesSection isAdmin={isAdmin} />
                </>
            )}
        </div>
    );
}

// ─── Members list ─────────────────────────────────────────────────────────

function MembersSection({
    isAdmin,
    selfClinicianId,
}: {
    isAdmin: boolean;
    selfClinicianId: string | null;
}) {
    const [search, setSearch] = useState('');
    const { data, isLoading, isError } = useQuery({
        queryKey: ['clinic-members'],
        queryFn: () => clinicApi.members(),
    });

    // Client-side filter — clinics rarely have more than a few dozen
    // members, so we can afford in-memory matching. Match against name,
    // phone, and the role label so an admin can jump to "all nurses"
    // just by typing the role.
    const filtered = useMemo(() => {
        const list = data?.members ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((m) => {
            const name = m.clinician_name?.toLowerCase() ?? '';
            const phone = m.phone_e164?.toLowerCase() ?? '';
            const roleLabel = (CLINIC_MEMBER_ROLE_LABELS[m.member.role] ?? m.member.role).toLowerCase();
            return name.includes(q) || phone.includes(q) || roleLabel.includes(q);
        });
    }, [data, search]);

    return (
        <section className="space-y-2">
            <h2 className="text-slate-800 text-lg font-semibold">Active members</h2>
            <label htmlFor="team-search" className="sr-only">
                Search members
            </label>
            <input
                id="team-search"
                type="search"
                className="pm-input"
                placeholder="Search by name, phone, or role"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            {isLoading && (
                <div className="pm-card p-4 text-slate-500 text-sm">Loading…</div>
            )}
            {isError && (
                <div className="pm-card p-4 text-slate-500 text-sm">Can’t load members.</div>
            )}
            {data && data.members.length > 0 && filtered.length === 0 && (
                <div className="pm-card p-4 text-slate-500 text-sm">
                    No members match “{search.trim()}”.
                </div>
            )}
            {data && filtered.length > 0 && (
                <div className="pm-card overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                        {filtered.map((m) => (
                            <MemberRow
                                key={m.member.clinician_id}
                                entry={m}
                                isAdmin={isAdmin}
                                isSelf={m.member.clinician_id === selfClinicianId}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

// Role options excluding 'provider' — that value is legacy-only and the
// invite/patch endpoints reject it, so don't offer it in the picker.
const ROLE_CHOICES = CLINIC_MEMBER_ROLE_OPTIONS as ReadonlyArray<
    Exclude<ClinicMemberRole, 'provider'>
>;

function MemberRow({
    entry,
    isAdmin,
    isSelf,
}: {
    entry: ClinicMemberWithClinician;
    isAdmin: boolean;
    isSelf: boolean;
}) {
    const qc = useQueryClient();
    const [error, setError] = useState<string | null>(null);

    const invalidateAll = () => {
        void qc.invalidateQueries({ queryKey: ['clinic-members'] });
        // Role change can flip 'my-clinic' state (e.g. someone else demoting me).
        void qc.invalidateQueries({ queryKey: ['my-clinic'] });
    };

    const changeRole = useMutation({
        mutationFn: (nextRole: Exclude<ClinicMemberRole, 'provider'>) =>
            clinicApi.changeRole(entry.member.clinician_id, nextRole),
        onSuccess: () => {
            setError(null);
            invalidateAll();
        },
        onError: (e) => setError(translateAdminError(e)),
    });

    const remove = useMutation({
        mutationFn: () => clinicApi.removeMember(entry.member.clinician_id),
        onSuccess: () => {
            setError(null);
            invalidateAll();
        },
        onError: (e) => setError(translateAdminError(e)),
    });

    // Show inline controls only for admins acting on someone else. Admin
    // editing their own row would just be a footgun (see cannot_demote_sole_admin).
    const showControls = isAdmin && !isSelf;
    const busy = changeRole.isPending || remove.isPending;

    const handleRemove = () => {
        const name = entry.clinician_name?.trim() || entry.phone_e164;
        if (!window.confirm(`Remove ${name} from the clinic? They will lose access immediately.`)) {
            return;
        }
        remove.mutate();
    };

    return (
        <li className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 font-medium">
                        {entry.clinician_name?.trim() || entry.phone_e164}
                        {isSelf && (
                            <span className="ml-2 text-xs text-slate-400 font-normal">(you)</span>
                        )}
                    </div>
                    <div className="text-slate-500 text-xs">{entry.phone_e164}</div>
                </div>
                {showControls ? (
                    <div className="flex items-center gap-2 shrink-0">
                        <label className="sr-only" htmlFor={`role-${entry.member.clinician_id}`}>
                            Change role
                        </label>
                        <select
                            id={`role-${entry.member.clinician_id}`}
                            className="pm-input text-sm py-1"
                            value={entry.member.role === 'provider' ? 'doctor' : entry.member.role}
                            onChange={(e) =>
                                changeRole.mutate(
                                    e.target.value as Exclude<ClinicMemberRole, 'provider'>,
                                )
                            }
                            disabled={busy}
                        >
                            {ROLE_CHOICES.map((r) => (
                                <option key={r} value={r}>
                                    {CLINIC_MEMBER_ROLE_LABELS[r]}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="pm-btn-ghost text-rose-600 text-sm"
                            onClick={handleRemove}
                            disabled={busy}
                        >
                            {remove.isPending ? 'Removing…' : 'Remove'}
                        </button>
                    </div>
                ) : (
                    <RoleBadge role={entry.member.role} />
                )}
            </div>
            {error && (
                <div className="text-rose-600 text-xs mt-2">{error}</div>
            )}
        </li>
    );
}

function RoleBadge({ role }: { role: ClinicMemberRole }) {
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
            {CLINIC_MEMBER_ROLE_LABELS[role] ?? role}
        </span>
    );
}

// ─── New invite ───────────────────────────────────────────────────────────
// Role selector is required — server rejects invites without one, so the
// UI enforces it too. Default 'doctor' matches the DB backfill.

function NewInviteForm() {
    const qc = useQueryClient();
    const [role, setRole] = useState<Exclude<ClinicMemberRole, 'provider'>>('doctor');
    const [nameHint, setNameHint] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [lastCreated, setLastCreated] = useState<{ code: string; shareText: string } | null>(null);

    const create = useMutation({
        mutationFn: () => clinicApi.createInvite({
            role,
            ...(nameHint.trim() ? { nameHint: nameHint.trim() } : {}),
        }),
        onSuccess: (res) => {
            setError(null);
            setNameHint('');
            setLastCreated({ code: res.invite.code, shareText: `You are invited to Pamosi Clinic as ${CLINIC_MEMBER_ROLE_LABELS[res.invite.role]}. Open ${window.location.origin}/login, choose Join a clinic with invite code, and enter ${res.invite.code}. Create your username and password; no OTP is required.` });
            void qc.invalidateQueries({ queryKey: ['clinic-invites'] });
        },
        onError: (e) => {
            if (e instanceof ApiError && e.code === 'invalid_role') {
                setError('Pick a role for this invite.');
            } else if (e instanceof ApiError && e.code === 'forbidden') {
                setError('Only admins can create invites.');
            } else {
                setError('Could not create invite. Try again.');
            }
        },
    });

    return (
        <section className="pm-card p-4 space-y-3">
            <h2 className="text-slate-800 text-lg font-semibold">Invite a new member</h2>
            <p className="text-slate-500 text-sm">
                Pick a role and share the 8-character invitation code directly with the staff member.
                On this clinic’s sign-in page, they choose Join a clinic with invite code
                and set up their username and password. No OTP is required.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm">
                    <span className="text-slate-700 font-medium">Role</span>
                    <select
                        className="pm-input w-full mt-1"
                        value={role}
                        onChange={(e) => setRole(e.target.value as typeof role)}
                        disabled={create.isPending}
                    >
                        {CLINIC_MEMBER_ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>{CLINIC_MEMBER_ROLE_LABELS[r]}</option>
                        ))}
                    </select>
                </label>
                <label className="text-sm">
                    <span className="text-slate-700 font-medium">Name hint (optional)</span>
                    <input
                        type="text"
                        className="pm-input w-full mt-1"
                        value={nameHint}
                        onChange={(e) => setNameHint(e.target.value)}
                        placeholder="e.g. Nurse Amaka"
                        maxLength={60}
                        disabled={create.isPending}
                    />
                </label>
            </div>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={() => create.mutate()}
                    disabled={create.isPending}
                >
                    {create.isPending ? 'Creating…' : 'Create invite'}
                </button>
                {error && <div className="text-rose-600 text-sm">{error}</div>}
            </div>

            {lastCreated && (
                <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-md p-3">
                    <div className="text-emerald-900 text-sm font-medium">
                        Invite ready. Code: <span className="font-mono">{lastCreated.code}</span>
                    </div>
                    <textarea
                        readOnly
                        className="pm-input w-full text-xs mt-2 bg-white"
                        rows={5}
                        value={lastCreated.shareText}
                        onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs mt-1"
                        onClick={() => {
                            void navigator.clipboard.writeText(lastCreated.shareText);
                        }}
                    >
                        Copy share text
                    </button>
                </div>
            )}
        </section>
    );
}

// ─── Invites list ─────────────────────────────────────────────────────────

function InvitesSection({ isAdmin }: { isAdmin: boolean }) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['clinic-invites'],
        queryFn: () => clinicApi.invites(),
    });
    return (
        <section className="space-y-2">
            <h2 className="text-slate-800 text-lg font-semibold">Invites</h2>
            {isLoading && (
                <div className="pm-card p-4 text-slate-500 text-sm">Loading…</div>
            )}
            {isError && (
                <div className="pm-card p-4 text-slate-500 text-sm">Can’t load invites.</div>
            )}
            {data && data.invites.length === 0 && (
                <div className="pm-card p-4 text-slate-500 text-sm">No invites yet.</div>
            )}
            {data && data.invites.length > 0 && (
                <div className="pm-card overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                        {data.invites.map((iv) => (
                            <InviteRow key={iv.id} invite={iv} isAdmin={isAdmin} />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

function InviteRow({ invite, isAdmin }: { invite: ClinicInviteListItem; isAdmin: boolean }) {
    const qc = useQueryClient();
    const [error, setError] = useState<string | null>(null);

    const revoke = useMutation({
        mutationFn: () => clinicApi.revokeInvite(invite.id),
        onSuccess: () => {
            setError(null);
            void qc.invalidateQueries({ queryKey: ['clinic-invites'] });
        },
        onError: (e) => setError(translateAdminError(e)),
    });

    const showRevoke = isAdmin && invite.status === 'pending';

    const handleRevoke = () => {
        const hint = invite.name_hint ? ` for ${invite.name_hint}` : '';
        if (!window.confirm(`Revoke this invite${hint}? The code will stop working immediately.`)) {
            return;
        }
        revoke.mutate();
    };

    return (
        <li className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-slate-900 text-sm flex items-center gap-2">
                        {invite.code
                            ? <span className="font-mono">{invite.code}</span>
                            : <span className="text-slate-400">(redeemed)</span>}
                        <RoleBadge role={invite.role} />
                    </div>
                    <div className="text-slate-500 text-xs mt-0.5">
                        {invite.name_hint ? `${invite.name_hint} · ` : ''}
                        {invite.status === 'pending' ? `expires ${formatWhen(invite.expires_at)}` : invite.status}
                    </div>
                </div>
                {showRevoke && (
                    <button
                        type="button"
                        className="pm-btn-ghost text-rose-600 text-sm shrink-0"
                        onClick={handleRevoke}
                        disabled={revoke.isPending}
                    >
                        {revoke.isPending ? 'Revoking…' : 'Revoke'}
                    </button>
                )}
            </div>
            {error && (
                <div className="text-rose-600 text-xs mt-2">{error}</div>
            )}
        </li>
    );
}

// Translate the server's admin-guardrail error codes into copy an admin can
// act on. The sole-admin cases point to the Transfer admin flow rather than
// dead-ending on "forbidden".
function translateAdminError(e: unknown): string {
    if (e instanceof ApiError) {
        switch (e.code) {
            case 'cannot_demote_sole_admin':
                return 'You are the only admin. Transfer admin to someone else first.';
            case 'cannot_revoke_sole_admin':
                return 'You are the only admin. Transfer admin to someone else first.';
            case 'target_not_member':
                return 'That member is no longer in the clinic. Refresh to update the list.';
            case 'invalid_role':
                return 'Pick a valid role.';
            case 'forbidden':
                return 'Only admins can change members.';
            case 'invite_not_revokable':
                return 'This invite has already been used or expired.';
            default:
                return 'Could not complete that action. Try again.';
        }
    }
    return 'Could not complete that action. Try again.';
}

function formatWhen(at: string): string {
    const t = new Date(at).getTime();
    if (!Number.isFinite(t)) return at;
    const diffMin = Math.round((t - Date.now()) / 60_000);
    if (Math.abs(diffMin) < 60) return `in ${diffMin}m`;
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return `in ${diffHr}h`;
    return new Date(at).toLocaleString();
}
